import type { QueryKey } from '@tanstack/react-query';
import type { AxiosError } from 'axios';
import type { PaginationExtendedData } from '#types';
import { type InfiniteData, type UseInfiniteQueryOptions, useInfiniteQuery } from './useInfiniteQuery';

export type OffsetInfiniteQueryNextPageParameterOptions<TQueryFunctionData extends PaginationExtendedData<unknown>> = {
  countsAsOffsetItem?: (item: TQueryFunctionData['data'][number]) => boolean;
  countsAsPageItem?: (item: TQueryFunctionData['data'][number]) => boolean;
};

export const useOffsetInfiniteQuery = <
  TQueryFunctionData extends PaginationExtendedData<unknown>,
  TError extends AxiosError = AxiosError,
  TData = InfiniteData<TQueryFunctionData>,
  TQueryKey extends QueryKey = QueryKey,
>({
  nextPageParam,
  ...options
}: {
  nextPageParam?: OffsetInfiniteQueryNextPageParameterOptions<TQueryFunctionData>;
} & Omit<
  UseInfiniteQueryOptions<TQueryFunctionData, TError, TData, TQueryKey, number>,
  'getNextPageParam' | 'initialPageParam'
>) =>
  useInfiniteQuery<TQueryFunctionData, number, TError, TData, TQueryKey>({
    getNextPageParam(lastPage, allPages) {
      const lastPageItemCount = lastPage.data.filter((item) => nextPageParam?.countsAsPageItem?.(item) ?? true).length;

      if (lastPageItemCount < lastPage.limit) {
        return null;
      }

      return allPages.flatMap((page) => page.data).filter((item) => nextPageParam?.countsAsOffsetItem?.(item) ?? true)
        .length;
    },
    initialPageParam: 0,
    ...options,
  });
